// src/store/projectArtifactStore.ts

/**
 * Project Artifact Store - in-memory home for a project's hydrated strategic
 * memory (blueprint, discovery, solution concept, implication, recommendation).
 *
 * Populated by hydrate(projectId) on project open (see initArtifactHydration.ts),
 * so the UI can render "everything Nuvra reasoned about this product" without each
 * component refetching. Cleared when a project unloads.
 */

import { create } from "zustand";
import { loadProjectArtifacts, EMPTY_ARTIFACT_BUNDLE, type ProjectArtifactBundle } from "@/platform/projectArtifacts";
import { logger } from "@/system/logger";

export type ArtifactHydrationStatus = "idle" | "loading" | "ready" | "error";

interface ProjectArtifactState {
  projectId: string | null;
  status:    ArtifactHydrationStatus;
  artifacts: ProjectArtifactBundle;
  /** Load the latest artifacts for a project into the store. */
  hydrate: (projectId: string) => Promise<void>;
  /** Reset to empty (project unloaded). */
  clear: () => void;
}

export const useProjectArtifactStore = create<ProjectArtifactState>((set, get) => ({
  projectId: null,
  status:    "idle",
  artifacts: { ...EMPTY_ARTIFACT_BUNDLE },

  hydrate: async (projectId: string) => {
    if (!projectId) return;
    set({ projectId, status: "loading" });
    try {
      const artifacts = await loadProjectArtifacts(projectId);
      // Ignore a stale response if the active project changed mid-flight.
      if (get().projectId !== projectId) return;
      set({ artifacts, status: "ready" });
      logger.info("[projectArtifactStore] hydrated", { projectId, hasAny: artifacts.hasAny });
    } catch (err) {
      if (get().projectId !== projectId) return;
      set({ status: "error" });
      logger.error("[projectArtifactStore] hydrate failed", { projectId, err });
    }
  },

  clear: () => set({ projectId: null, status: "idle", artifacts: { ...EMPTY_ARTIFACT_BUNDLE } }),
}));
